import {Plural, Trans} from '@lingui/macro';
import React, {FC, ReactElement, ReactNode} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {NamesFormatter} from '../../../../../srcCommon/formatters/namesFormatter';
import {CommentWarning, CommentWarningTypesEnum} from '../../../../helpers/models/commentHelpers';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {getHasGuestInviteNewCopy} from '../../../../store/featureFlags/featureFlagsSelectors';
import {TeammateAvatar} from '../../../common/avatars/teammateAvatar';
import {VisitorTeammateAvatar} from '../../../common/avatars/visitorTeammateAvatar';
import {OkCancelDialog} from '../../../common/dialogs/okCancelDialog';
import {TeammatesFormatter} from '../../../common/formatters/teammatesFormatter';

/*
 * Props.
 */

interface CommentComposerWarningDialogProps {
  warning: CommentWarning;
  onRequestClose: Handler;
  onOkClick: Handler;
  isInConversationWithDrafts: boolean;
}

/*
 * Style.
 */

const StyledAvatarDiv = styled.div`
  margin-right: 6px;
  align-self: flex-start;
`;

const StyledNameWithAvatarDiv = styled.div`
  display: inline-flex;
  align-items: baseline;
`;

/*
 * Component.
 */

export const CommentComposerWarningDialog: FC<CommentComposerWarningDialogProps> = (props) => {
  const hasGuestInviteNewCopy = useAppSelector(getHasGuestInviteNewCopy);
  const localizedWarning = buildLocalizedCommentWarning(
    props.warning,
    props.isInConversationWithDrafts,
    hasGuestInviteNewCopy,
  );
  return (
    <OkCancelDialog {...localizedWarning} {...props}>
      {localizedWarning.content}
    </OkCancelDialog>
  );
};

/*
 * Localization.
 */

interface LocalizedCommentWarning {
  title: ReactNode;
  okButtonLabel: ReactNode;
  content: ReactNode;
  cancelButtonLabel?: ReactNode;
}

function buildLocalizedCommentWarning(
  warning: CommentWarning,
  isInConversationWithDrafts: boolean,
  hasGuestInviteNewCopy: boolean,
): LocalizedCommentWarning {
  switch (warning.type) {
    case CommentWarningTypesEnum.NO_TOPIC:
      return {
        title: <Trans id="warning-notopic-title">Missing topic</Trans>,
        okButtonLabel: <Trans id="warning-notopic-ok">Create</Trans>,
        content: (
          <Trans id="warning-notopic-content">
            This discussion has no topic.
            <br />
            Would you like to create it anyway?
          </Trans>
        ),
      };

    case CommentWarningTypesEnum.MENTION_ALL:
      return {
        title: <Trans id="warning-mentionall-title">Mention everyone</Trans>,
        okButtonLabel: <Trans id="warning-mentionall-ok">Continue</Trans>,
        content: renderMentionAllWarningContent(warning.teammatesCount),
      };

    case CommentWarningTypesEnum.MENTION_NO_ACCESS:
      if (isInConversationWithDrafts) {
        return {
          title: <Trans id="warning-share-title">Share conversation</Trans>,
          okButtonLabel: <Trans id="warning-share-ok">Share</Trans>,
          content: (
            <Plural
              id="warning-autodraftshare-content"
              value={warning.teammates.length}
              one={
                <>
                  <TeammatesFormatter
                    teammates={warning.teammates}
                    renderTeammateName={(name, id) =>
                      renderTeammateName(
                        name,
                        warning.teammates.find((t) => t.id === id),
                      )
                    }
                  />{' '}
                  does not have access to this conversation. Would you like to give them access to your drafts
                  and all existing and future messages in this conversation?
                </>
              }
              other={
                <>
                  <TeammatesFormatter
                    teammates={warning.teammates}
                    renderTeammateName={(name, id) =>
                      renderTeammateName(
                        name,
                        warning.teammates.find((t) => t.id === id),
                      )
                    }
                  />{' '}
                  do not have access to this conversation. Would you like to give them access to your drafts
                  and all existing and future messages in this conversation?
                </>
              }
            />
          ),
        };
      }

      return {
        title: <Trans id="warning-share-title">Share conversation</Trans>,
        okButtonLabel: <Trans id="warning-share-ok">Share</Trans>,
        content: (
          <Plural
            id="warning-share-content"
            value={warning.teammates.length}
            one={
              <>
                <TeammatesFormatter
                  teammates={warning.teammates}
                  renderTeammateName={(name, id) =>
                    renderTeammateName(
                      name,
                      warning.teammates.find((t) => t.id === id),
                    )
                  }
                />{' '}
                does not have access to this conversation. Would you like to give them access to all existing
                and future messages in this conversation?
              </>
            }
            other={
              <>
                <TeammatesFormatter
                  teammates={warning.teammates}
                  renderTeammateName={(name, id) =>
                    renderTeammateName(
                      name,
                      warning.teammates.find((t) => t.id === id),
                    )
                  }
                />{' '}
                do not have access to this conversation. Would you like to give them access to all existing
                and future messages in this conversation?
              </>
            }
          />
        ),
      };

    case CommentWarningTypesEnum.MENTION_VISITOR:
      if (hasGuestInviteNewCopy) {
        return {
          title: <Trans id="warning-guest-title-new">Invite guests</Trans>,
          okButtonLabel: (
            <Plural
              id="warning-guest-ok-new"
              value={warning.emails.length}
              one={<>Invite guest</>}
              other={<>Invite guests</>}
            />
          ),
          content: (
            <Plural
              id="warning-guest-content-new"
              value={warning.emails.length}
              one={
                <>
                  <NamesFormatter names={warning.emails} renderName={renderVisitorName} /> will be invited as
                  Guest and will be allowed to read the conversation, post comments, and edit drafts. Would
                  you like to continue?
                </>
              }
              other={
                <>
                  <NamesFormatter names={warning.emails} renderName={renderVisitorName} /> will be invited as
                  Guest and will be allowed to read the conversation, post comments, and edit drafts. Would
                  you like to continue?
                </>
              }
            />
          ),
          cancelButtonLabel: <Trans id="warning-guest-cancel-new">Cancel</Trans>,
        };
      }
      // Old copy.
      return {
        title: <Trans id="warning-guest-title-old">Invite guests</Trans>,
        okButtonLabel: (
          <Plural
            id="warning-guest-ok-old"
            value={warning.emails.length}
            one={<>Invite guest</>}
            other={<>Invite guests</>}
          />
        ),
        content: (
          <Plural
            id="warning-guest-content-old"
            value={warning.emails.length}
            one={
              <>
                <NamesFormatter names={warning.emails} renderName={renderVisitorName} /> does not have access
                to this conversation. They will be invited as Guest and will be allowed to read the
                conversation, post comments, and edit drafts. Would you like to continue?
              </>
            }
            other={
              <>
                <NamesFormatter names={warning.emails} renderName={renderVisitorName} /> do not have access to
                this conversation. They will be invited as Guest and will be allowed to read the conversation,
                post comments, and edit drafts. Would you like to continue?
              </>
            }
          />
        ),
        cancelButtonLabel: <Trans id="warning-guest-cancel-old">Cancel</Trans>,
      };

    case CommentWarningTypesEnum.MENTION_VISITOR_IN_TRIAL:
      return {
        title: (
          <Plural
            id="warning-mentionvisitortrial-title"
            value={warning.emails.length}
            one={<>Cannot invite guest</>}
            other={<>Cannot invite guests</>}
          />
        ),
        okButtonLabel: <Trans id="warning-mentionvisitortrial-ok">OK</Trans>,
        content: (
          <Plural
            id="warning-mentionvisitortrial-content"
            value={warning.emails.length}
            one={
              <>
                Looks like you’re trying to invite{' '}
                <NamesFormatter names={warning.emails} renderName={renderVisitorName} /> as a guest! That
                feature is only available for paid customers. You can still post this comment, but{' '}
                <NamesFormatter names={warning.emails} renderName={renderVisitorName} /> will not have access
                to this conversation.
              </>
            }
            other={
              <>
                Looks like you’re trying to invite{' '}
                <NamesFormatter names={warning.emails} renderName={renderVisitorName} /> as guests! That
                feature is only available for paid customers. You can still post this comment, but{' '}
                <NamesFormatter names={warning.emails} renderName={renderVisitorName} /> will not have access
                to this conversation.
              </>
            }
          />
        ),
      };

    default:
      throw new Error('This should not happen.');
  }
}

function renderMentionAllWarningContent(teammatesCount: number) {
  switch (teammatesCount) {
    case 0:
      return (
        <Trans id="warning-mentionallnoteammates-content">
          No one else is on this conversation. Mentioning <strong>@all</strong> will have no effect. Would you
          like to continue?
        </Trans>
      );

    case 1:
      return (
        <Trans id="warning-mentionallone-content">
          Mentioning <strong>@all</strong> will notify <strong>one teammate</strong> with access to this
          conversation. Would you like to continue?
        </Trans>
      );

    default:
      return (
        <Trans id="warning-mentionallmany-content">
          Mentioning <strong>@all</strong> will notify <strong>{teammatesCount} teammates</strong> with access
          to this conversation. Would you like to continue?
        </Trans>
      );
  }
}

/*
 * Helpers.
 */

function renderTeammateName(name: ReactNode, teammate?: TeammateModel): ReactElement {
  return (
    <StyledNameWithAvatarDiv>
      {teammate !== undefined && (
        <StyledAvatarDiv>
          <TeammateAvatar size={VisualSizesEnum.SMALL} teammate={teammate} />
        </StyledAvatarDiv>
      )}
      <strong>{name}</strong>
    </StyledNameWithAvatarDiv>
  );
}

function renderVisitorName(name: ReactNode): ReactElement {
  return (
    <StyledNameWithAvatarDiv>
      <StyledAvatarDiv>
        <VisitorTeammateAvatar size={VisualSizesEnum.SMALL} initials={`${name}`} />
      </StyledAvatarDiv>
      <strong>{name}</strong>
    </StyledNameWithAvatarDiv>
  );
}
